import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

const THEME_TAXONOMY: Array<{ key: string; label: string; hint: string }> = [
  { key: 'conflict', label: 'Conflict', hint: 'Tension between people, teams, or priorities.' },
  { key: 'priorities', label: 'Priorities', hint: 'Clarity (or lack of) about what matters most.' },
  { key: 'growth', label: 'Growth', hint: 'Career, learning, and stretch opportunities.' },
  { key: 'recognition', label: 'Recognition', hint: 'Being seen and credited for the work.' },
  { key: 'workload', label: 'Workload', hint: 'Capacity, pace, burnout signals.' },
  { key: 'leadership', label: 'Leadership', hint: 'Trust and effectiveness up the chain.' },
  { key: 'collaboration', label: 'Collaboration', hint: 'How well teams work across functions.' },
  { key: 'communication', label: 'Communication', hint: 'Information flow and clarity.' },
  { key: 'autonomy', label: 'Autonomy', hint: 'Freedom in how work gets done.' },
  { key: 'belonging', label: 'Belonging', hint: 'Inclusion, safety, and identity.' },
];

export default function Themes() {
  const { orgId } = useOutletContext<{ orgId: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('org_settings')
        .select('themes')
        .eq('org_id', orgId)
        .maybeSingle();
      if (!active) return;
      const list = Array.isArray(data?.themes) ? (data!.themes as string[]) : [];
      setSelected(new Set(list));
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [orgId]);

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function onSave() {
    setSaving(true);
    const themes = Array.from(selected);
    const { error } = await supabase
      .from('org_settings')
      .upsert(
        {
          org_id: orgId,
          themes,
          updated_at: new Date().toISOString(),
          updated_by: user?.id ?? null,
        },
        { onConflict: 'org_id' },
      );
    setSaving(false);
    if (error) {
      toast({ title: 'Could not save themes', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Themes saved' });
    navigate('/app/admin/insights');
  }

  if (loading) {
    return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-semibold">Insight themes</h2>
        <p className="text-sm text-muted-foreground">
          Pick the themes your org cares about. Charles will tag conversations against these, and insights will roll up by theme.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Taxonomy</CardTitle>
          <CardDescription>Selected: {selected.size}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:grid-cols-2">
            {THEME_TAXONOMY.map((t) => {
              const checked = selected.has(t.key);
              return (
                <label
                  key={t.key}
                  className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/40"
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggle(t.key)} className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">{t.label}</div>
                    <div className="text-xs text-muted-foreground">{t.hint}</div>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="flex items-center justify-end gap-2 pt-5">
            <Button variant="ghost" onClick={() => navigate('/app/admin/insights')}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={saving || selected.size === 0}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save themes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
